import * as Comlink from 'comlink';
import { MAP_TOOL_ACTIONS, getActionsFromToolConfig } from '../tool_actions/map-tool-action-registry.js';
import { FirebaseConfig } from '../../../modules/FirebaseConfig.js';
/** @import { MapToolsWorkerApi } from '../../../workers/map-tools-worker/map-tools-worker.js' */
/** @import { Remote } from 'comlink' */

/**
 * @typedef {object} MapToolResult
 * @property {'completed' | 'aborted' | 'error'} status How the tool action ended: ran to completion, was torn down, or failed.
 * @property {any} data The value the tool action returned, if any.
 * @property {?Error} error The error that ended the tool action, or null when it did not fail.
 */

/**
 * Starts a map tool on the client and returns a cancel function and a promise for the result
 *
 * @param {object} toolConfig - The tool's configuration from the database set by the model editor
 * @returns {Promise<{ instanceId: string, cancelTool: () => void, completed: Promise<MapToolResult> }>} The tool instance id,
 * a `cancelTool` function that stops the tool and terminates its worker, and a `completed` promise the caller awaits for the result.
 */
export async function startMapToolOnClient(toolConfig) {
  // Generate a unique id for this tool instance
  const instanceId = crypto.randomUUID();

  // Decide which tool action to run from the tool's configuration, failing when none apply
  const toolActions = getActionsFromToolConfig(toolConfig);
  if (toolActions.length == 0) throw new Error('Tool config does not match any known tool action');
  if (toolActions.length > 1) throw new Error('Tool config matches multiple tool actions, which is not allowed');

  // Start the action and get back a cancel function and a completed promise for the running tool
  const toolActionId = toolActions[0];
  const { cancelTool, completed } = await runToolAction(toolActionId, toolConfig);

  return { instanceId, cancelTool, completed };
}

/**
 * Loads the client file for orchestration, loads the tool action's services in a worker, the runs the client file
 * and returns a cancel function to stop the tool and a completed promise for the tool's result
 *
 * @param {string} toolActionId
 * @param {object} toolConfig
 * @returns {Promise<{ cancelTool: () => void, completed: Promise<MapToolResult> }>}
 */
async function runToolAction(toolActionId, toolConfig) {
  const mapToolAction = MAP_TOOL_ACTIONS[toolActionId];
  if (!mapToolAction) throw new Error(`No tool action found for id "${toolActionId}"`);

  // Load the services and create the worker if the action has them
  const { services = {}, mapToolActionWorker } = (await loadToolActionServices(toolActionId)) ?? {};

  // Load the action's client module on the main thread, cleaning up the worker if it fails to load
  const clientModule = await loadToolActionClient(toolActionId).catch((error) => {
    mapToolActionWorker?.terminate();
    throw error;
  });

  // Signal used to stop the client when the tool is cancelled
  const abortController = new AbortController();
  const cancelTool = () => abortController.abort();

  // Start the tool action without awaiting it so the cancel function can be returned right away
  const toolActionPromise = (async () => clientModule.default(abortController, toolConfig, services))();

  // Settle the tool action into a result the caller can await, then terminate its worker
  const completed = settleToolAction(toolActionPromise, abortController.signal);
  completed.finally(() => mapToolActionWorker?.terminate());

  // Return a cancel function to stop the tool and a completed promise that resolves with its outcome
  return { cancelTool, completed };
}

/**
 * Awaits a running tool action and normalizes how it ended into a result object.
 * A failure after the tool is cancelled is reported as aborted rather than an error.
 *
 * @param {Promise<any>} toolActionPromise
 * @param {AbortSignal} signal
 * @returns {Promise<MapToolResult>}
 */
async function settleToolAction(toolActionPromise, signal) {
  try {
    // The tool finished on its own or returned after being cancelled
    const data = await toolActionPromise;
    if (signal.aborted) return { status: 'aborted', data, error: null };
    return { status: 'completed', data, error: null };
  } catch (error) {
    // A failure during cancellation is expected; any other failure is a real error
    if (signal.aborted) return { status: 'aborted', data: null, error: null };
    return { status: 'error', data: null, error };
  }
}

/**
 * Sets up a worker and loads the services module for the given tool action id if it exists
 *
 * @param {string} toolActionId
 * @returns {Promise<object>}
 */
async function loadToolActionServices(toolActionId) {
  const mapToolActionHasServices = MAP_TOOL_ACTIONS[toolActionId].loadServices != null;
  if (!mapToolActionHasServices) return;

  // Set up the worker and wrap it to get a proxy to its exposed API
  const mapToolActionWorker = new Worker('/source/_resources/workers/map-tools-worker/map-tools-worker.js', { type: 'module' });
  /** @type {Remote<MapToolsWorkerApi>} */
  const workerApi = Comlink.wrap(mapToolActionWorker);

  // Init firebase and load the services in the worker, terminating the worker if setup fails
  try {
    await workerApi.initFirebase(getFirebaseConfig());
    const services = await workerApi.loadToolActionServices(toolActionId);
    return { services, mapToolActionWorker };
  } catch (error) {
    mapToolActionWorker.terminate();
    throw error;
  }
}

/**
 * Dynamically imports the client module for the given tool action id and
 * enforces that it exposes a default function.
 *
 * @param {string} toolActionId
 * @returns {Promise<object>}
 */
async function loadToolActionClient(toolActionId) {
  // Enforce the contract that every action's client module exports a default function
  const clientModule = await MAP_TOOL_ACTIONS[toolActionId].loadClient();
  const clientModuleIsInvalid = clientModule == null || typeof clientModule.default !== 'function';
  if (clientModuleIsInvalid) throw new Error(`Tool action client for "${toolActionId}" must export a default function`);

  return clientModule;
}

/**
 * Reads the live Firebase config for the current app so it can be passed to the
 * worker for Firebase initialization.
 *
 * @returns {object}
 */
function getFirebaseConfig() {
  return FirebaseConfig(/** @type {any} */ (globalThis).config.appName);
}
